import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import React from "react";
import { Trans } from "next-i18next";

function Mobile() {
  const { t } = useTrans(["tactsuit"]);
  return (
    <div className="flex flex-col items-center laptop:hidden">
      <Head text={t("TactVisor Size Guide")} />
      <CompatibleNotice className="mt-5 text-center" />
      <Chart className="mt-7" />

      <div className="w-full">
        <Head
          className="mt-7 text-base font-600"
          text={t("Don’t see your VR HMD in the list?")}
        />
        <p className="mt-3 text-base">
          {t(
            "We cannot guarantee the compatibility/fitting with a VR HMD that is not listed in the chart above.",
          )}
        </p>
      </div>
    </div>
  );
}

const Head = ({ text, className }: { text: string; className?: string }) => (
  <h2 className={cn("text-3xl text-coolgray-90 laptop:text-5xl", className)}>
    {text}
  </h2>
);

interface AProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {}
const A = ({ className, ...rest }: AProps) => (
  <a
    className={cn("underline", className)}
    target="_blank"
    rel="noopener noreferrer"
    {...rest}
  />
);

const CompatibleNotice = ({ className }: { className?: string }) => {
  const { t } = useTrans(["tactsuit"]);
  return (
    <div className={cn("text-base text-coolgray-60", className)}>
      <Trans
        t={t}
        i18nKey="*Facial Interface from <vrcover>VR Cover</vrcover> is required for <valve_index>Valve Index</valve_index>, <meta_1>Meta Quest 1</meta_1>, <meta_2>Meta Quest 2</meta_2>, <meta_3>Meta Quest 3</meta_3>, <meta_3s>Meta Quest 3S</meta_3s> and <meta_rifts>Rift S</meta_rifts>."
        defaults="*Facial Interface from VR Cover is required for Valve Index, Meta Quest 1, 2, 3, 3S and Rift S."
        components={{
          vrcover: (
            <a href="https://vrcover.com/" target="_blank" rel="noreferrer" />
          ),
          valve_index: (
            <a
              href="https://vrcover.com/product/valve-index-vr-cover/"
              target="_blank"
              rel="noreferrer"
            />
          ),
          meta_1: (
            <a
              href="https://vrcover.com/product/oculus-quest-vr-cover/"
              target="_blank"
              rel="noreferrer"
            />
          ),
          meta_2: (
            <a
              href="https://vrcover.com/product/vr-cover-for-meta-oculus-quest-2/"
              target="_blank"
              rel="noreferrer"
            />
          ),
          meta_3: (
            <a
              href="https://vrcover.com/product/vr-cover-for-meta-quest-3/"
              target="_blank"
              rel="noreferrer"
            />
          ),
          meta_3s: (
            <a
              href="https://vrcover.com/product/vr-cover-for-meta-quest-3s/"
              target="_blank"
              rel="noreferrer"
            />
          ),
          meta_rifts: (
            <a
              href="https://vrcover.com/product/rift-s-vr-cover/"
              target="_blank"
              rel="noreferrer"
            />
          ),
        }}
      />
    </div>
  );
};

const Data = [
  ["Size", "Outer Measurement", "Inner Measurement", "Compatible VR HMDs"],
  [
    "Standard",
    "23.4cm x 10.9cm\n(9.2” x 4.3”)",
    "19.4cm x 5.8cm\n(7.6” x 2.3”)",
    "Valve Index*, Quest 2*, 3*, 3S* and Rift CV1*",
  ],
];

const Chart = ({ className }: { className: string }) => {
  const { t } = useTrans(["tactsuit"]);
  const DataTranslated = Data.map((row) => row.map((item) => t(item)));
  return (
    <div className={cn(className)}>
      <div className="grid grid-flow-row auto-rows-auto">
        {DataTranslated.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className={"grid auto-cols-fr grid-flow-col first:bg-coolgray-10"}
          >
            {row.map((cell, cellIndex) => (
              <p
                key={cellIndex}
                className="whitespace-break-spaces break-all p-2 text-sm laptop:px-4 laptop:py-5 laptop:text-base"
              >
                {t(cell)}
              </p>
            ))}
          </div>
        ))}
      </div>
      <span className="block h-[2px] w-full bg-coolgray-10" />
    </div>
  );
};

function Desktop() {
  const { t } = useTrans(["tactsuit"]);

  return (
    <div className="hidden laptop:block">
      <Head text={t("TactVisor Size Guide")} />
      <CompatibleNotice className="mt-5" />
      <Chart className="mt-7.5" />
      <Head
        className="mt-9 font-600 text-coolgray-90 laptop:text-lg"
        text={t("Don’t see your VR HMD in the list?")}
      />
      <p className="mt-3 text-base">
        {t(
          "We cannot guarantee the compatibility/fitting with a VR HMD that is not listed in the chart above.",
        )}
      </p>
    </div>
  );
}

export default function TactvisorSizeGuide() {
  return (
    <>
      <Desktop />
      <Mobile />
    </>
  );
}
